/* eslint-disable max-lines-per-function */
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import '@adornis/data-view/client/x-data-view.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import '@adornis/forms/x-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { filter, map, switchMap } from 'rxjs';
import '../../../../components/bh-dropdown-action-content.js';
import './bh-page--admin-einrichtung-counseling-modify.js';
import './bh-page-admin-einrichtung-counseling-edit.js';

const modes = ['data', 'participants'] as const;
type Mode = (typeof modes)[number];

const modeComponents = (viewRef, mode: Mode, counselingID: string, institutionID: string) => {
  if (mode === 'data')
    return html`
      <bh-page-admin-einrichtung-counseling-modify
        ${ref(viewRef)}
        institution-id=${institutionID}
        counseling-id=${counselingID}
        mode="edit"
      ></bh-page-admin-einrichtung-counseling-modify>
    `;

  if (mode === 'participants') {
    return html`
      <bh-page-admin-einrichtung-counseling-edit
        ${ref(viewRef)}
        counseling-id=${counselingID}
        institution-id=${institutionID}
      ></bh-page-admin-einrichtung-counseling-edit>
    `;
  }
};

/**
 * @element bh-page-admin-einrichtung-counseling-detail
 */
@customElement('bh-page-admin-einrichtung-counseling-detail')
export class BHPageAdminEinrichtungCounselingDetail extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: 'counseling-id' }) counselingID = new RXController<string>(this, '');

  @state() private readonly _counseling = new RXController(
    this,
    this.counselingID.observable.pipe(
      filter(Boolean),
      switchMap(id => subscribeByID(Einzelberatung, id)({ documents: AdornisFile.allFields, name: 1 })),
    ),
  );

  @state() private readonly viewMode = new RXController(
    this,
    routingState.searchParams.pipe(map(params => params.get('mode') as Mode)),
  );

  private activeViewRef = createRef();

  override render() {
    const counselingID = this.counselingID.value;
    const institutionID = this.institutionID;
    console.log('render view ', {
      counselingID,
      institutionID,
      viewMode: this.viewMode.value,
      counseling: this._counseling.value,
    });
    const viewMode = this.viewMode.isLoading ? null : this.viewMode.value ?? 'data';
    if (!counselingID || !this._counseling.value || !institutionID || !viewMode) {
      if (!this.viewMode.value) console.warn('no view mode');
      return html` <x-loading-state></x-loading-state> `;
    }

    const modeComponent = modeComponents(this.activeViewRef, viewMode, counselingID, institutionID);

    return html`
      <x-flex space="xl">
        <x-tabs .selected=${viewMode}>
          ${modes.map(
            mode =>
              html`
                <x-tab label=${mode} @click=${() => this.changeTabs(mode)}>
                  ${this._translation.translate('PRODUCT', 'editMode', mode)}
                </x-tab>
              `,
          )}
        </x-tabs>

        ${modeComponent}
      </x-flex>
    `;
  }

  async changeTabs(mode: Mode) {
    const searchParams = new URLSearchParams();
    searchParams.append('mode', mode);
    await goTo({ path: NO_CHANGE, searchParams });
  }
}
